/// Online pass sale (specs/online-sale.md) and a club's place on «Зали
/// поруч» (specs/partner-clubs.md) in the panel — its own file, one line in
/// uk.ts, so that sections built in parallel merge cleanly.

/// A copy of uk.ts's `plural` — importing it would make the two files a cycle.
const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const orders = (n: number) => `${n} ${plural(n, 'замовлення', 'замовлення', 'замовлень')}`

export const saleUk = {
  payment: {
    section: 'Онлайн-оплата',
    help:
      'Клієнти купують абонементи в застосунку eatlete й платять карткою. Гроші йдуть одразу на ваш рахунок WayForPay — eatlete їх не тримає й комісії не бере.',
    receipts:
      'Фіскальні чеки (ПРРО) — ваші: їх налаштовують у кабінеті WayForPay або Checkbox. Ми передаємо в чек назву тарифу й ціну.',
    whereToFind: 'Логін мерчанта й секретний ключ — в особистому кабінеті WayForPay, у налаштуваннях вашого магазину.',
    state: 'Зараз',
    states: {
      ready: 'Приймаємо оплату',
      off: 'Вимкнено',
      incomplete: 'Не налаштовано',
      unreadable: 'Ключ не читається — введіть його ще раз',
    } as Record<string, string>,
    enable: 'Продавати абонементи в застосунку',
    enableHint: 'Діє для тарифів, у яких увімкнено «Продається в застосунку».',
    enableNeeds: 'Спершу вкажіть логін мерчанта, домен і секретний ключ.',
    merchant: 'Мерчант WayForPay',
    account: 'Логін мерчанта',
    accountPlaceholder: 'd_fitness_com_ua',
    domain: 'Домен магазину',
    domainPlaceholder: 'd-fitness.com.ua',
    domainHint: 'Як у кабінеті WayForPay, без https://',
    domainInvalid: 'Вкажіть домен, наприклад d-fitness.com.ua',
    secret: 'Секретний ключ',
    secretSet: (last4: string | null) => (last4 ? `Встановлено ••••${last4}` : 'Встановлено'),
    secretNotSet: 'Не встановлено',
    secretPlaceholderNew: 'Вставте ключ з кабінету WayForPay',
    secretPlaceholderReplace: 'Новий ключ — лише якщо міняєте',
    secretHint: 'Ключ ніхто не побачить, навіть ви: його зберігають зашифрованим і нікому не показують.',
    secretShort: 'Ключ занадто короткий — скопіюйте його повністю',
    secretRemove: 'Видалити ключ',
    secretRemoveTitle: 'Видалити секретний ключ?',
    secretRemoveMessage: 'Онлайн-оплату буде вимкнено, доки не введете ключ знову. Уже продані абонементи не зміняться.',
    secretRemoveConfirm: 'Видалити',
    edit: 'Змінити',
    editTitle: 'Мерчант WayForPay',
    readOnly: 'Змінювати онлайн-оплату може роль із «Фінанси: керування» — зазвичай власник або бухгалтер.',
    saved: 'Онлайн-оплату збережено',
    turnedOn: 'Онлайн-оплату ввімкнено — тарифи з позначкою «Продається в застосунку» вже в продажу',
    turnedOff: 'Онлайн-оплату вимкнено',
    notConfiguredServer: 'Онлайн-оплата на сервері ще не налаштована. Напишіть команді eatlete.',
    attentionCount: (n: number) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'потребує' : 'потребують'} уваги`,
    attentionLink: (n: number) => `${orders(n)} з застосунку ${n % 10 === 1 && n % 100 !== 11 ? 'потребує' : 'потребують'} уваги`,
  },

  plan: {
    online: 'Продається в застосунку',
    onlineHint: 'Клієнти побачать тариф у застосунку eatlete й зможуть купити його онлайн.',
    onlineNeedsPrice: 'Безкоштовний тариф не продається онлайн — вкажіть ціну.',
    onlineNeedsPayment: 'Спершу налаштуйте онлайн-оплату в «Налаштуваннях».',
    onlineCheckPayment: 'Продаж запрацює, коли онлайн-оплату ввімкнуть у «Налаштуваннях».',
    onlineOffAfterSave: 'Без ціни тариф не продаватиметься в застосунку — вимкніть перемикач або вкажіть ціну.',
    badge: 'У застосунку',
  },

  sold: {
    source: 'Звідки',
    app: 'Застосунок',
    desk: 'Каса',
    online: 'Онлайн',
    paidOnline: 'Оплачено онлайн',
    soldIn: 'Продано',
    payment: 'Оплата',
    reference: 'Замовлення',
  },

  orders: {
    title: 'Замовлення з застосунку',
    open: 'Замовлення з застосунку',
    look: 'Переглянути',
    subtitle: 'Покупки абонементів у застосунку. Оплачені вже лежать у «Проданих».',
    filters: { attention: 'Потребують уваги', pending: 'Очікують оплати', failed: 'Не оплачені', all: 'Усі' } as Record<
      string,
      string
    >,
    empty: 'Тут порожньо.',
    emptyAttention: 'Усе гаразд — кожна оплата стала абонементом.',
    status: {
      pending: 'Очікує оплати',
      paid: 'Оплачено',
      failed: 'Не оплачено',
      expired: 'Час вийшов',
      needs_attention: 'Потребує уваги',
    } as Record<string, string>,
    reason: {
      declined: 'банк відхилив',
      expired: 'час на оплату вийшов',
      invoice_failed: 'WayForPay не створив рахунок',
      amount_mismatch: 'сума оплати не збігається з ціною',
      plan_changed: 'тариф змінили під час оплати',
      unknown_plan: 'тариф видалено під час оплати',
      discount_not_allowed: 'сума менша за ціну тарифу без знижок',
      client_inactive: 'картку клієнта архівовано',
      plan_retired: 'тариф знято з продажу',
      plan_period_over: 'строк тарифу минув',
      not_new_client: 'тариф лише для нових клієнтів',
      client_too_young: 'клієнт замолодий для тарифу',
      client_too_old: 'клієнт застарий для тарифу',
      birth_date_required: 'немає дати народження',
    } as Record<string, string>,
    /// WayForPay's `reasonCode` on a declined payment (wiki.wayforpay.com
    /// «Коди відповідей»): what the club can tell the buyer. Unknown codes
    /// show the provider's own words.
    provider: {
      1101: 'банк відхилив оплату',
      1102: 'невірний CVV-код',
      1103: 'термін дії картки минув або вказаний невірно',
      1104: 'недостатньо коштів на картці',
      1105: 'невірний номер картки або картка заблокована для оплати',
      1106: 'перевищено ліміт операцій за карткою — можливо, не відкриті інтернет-оплати',
      1108: 'не пройдено перевірку 3-D Secure',
      1114: 'відхилено антифрод-фільтром',
      1118: 'обмеження магазину WayForPay',
      1120: '3-D Secure недоступний для картки',
      1122: 'платіжний шлюз відхилив оплату',
      1124: 'час на оплату минув',
      1125: 'покупець скасував оплату',
      1135: 'перевищено ліміт за карткою',
      1146: 'картку заблоковано банком',
    } as Record<number, string>,
    problem: {
      bad_signature: 'прийшло повідомлення з неправильним підписом',
      merchant_changed: 'оплата прийшла на попередній мерчант',
      key_unreadable: 'ключ мерчанта не читається',
    } as Record<string, string>,
    attentionNote:
      'Гроші могли надійти, а абонемента немає. Перевірте оплату в кабінеті WayForPay за номером замовлення: продайте абонемент на касі або поверніть гроші там же.',
    columns: { when: 'Коли', plan: 'Тариф', amount: 'Сума', state: 'Стан' },
    card: (pan: string) => `картка ${pan}`,
  },

  location: {
    section: 'На мапі eatlete',
    hint:
      'Клуб із місцем на мапі показується першим у «Залах поруч» у застосунку — з вашим логотипом і кнопкою «Купити абонемент».',
    none: 'Ще не на мапі',
    on: 'На мапі',
    linked: 'Привʼязано до місця на мапі',
    pick: 'Знайти свій зал на мапі',
    pickTitle: 'Це наш зал',
    pickHint: 'Пошук серед залів на мапі за назвою або адресою. Оберіть свій — візьмемо його точку.',
    search: 'Назва або адреса',
    searchShort: 'Введіть хоча б 2 літери',
    searching: 'Шукаємо…',
    nothing: 'Нічого не знайшли. Спробуйте іншу назву або вкажіть координати вручну.',
    choose: 'Це наш',
    chosen: 'Обрано',
    current: 'Ваше місце зараз',
    currentHint: 'У пошуку його немає: на мапі це місце вже показано під вашим пином із логотипом.',
    manual: 'Ввести координати вручну',
    lat: 'Широта',
    lon: 'Довгота',
    latPlaceholder: '48.450367',
    lonPlaceholder: '22.705463',
    manualHint: 'Можна вставити обидва числа одразу, як з Google Maps: «48.450367, 22.705463».',
    pairInvalid: 'Потрібні і широта, і довгота',
    latInvalid: 'Широта — число від −90 до 90',
    lonInvalid: 'Довгота — число від −180 до 180',
    openMap: 'Відкрити на мапі',
    clear: 'Прибрати з мапи',
    unavailable: 'Мапа залів зараз недоступна. Координати можна ввести вручну.',
    far: (km: string) => `${km} км від клубу`,
  },

  errors: {
    onlineSaleNeedsPrice: 'Безкоштовний тариф не продається в застосунку — вкажіть ціну або вимкніть «Продається в застосунку»',
    onlineSaleNeedsUah: 'У застосунку продаються лише тарифи в гривнях',
    paymentSettingsIncomplete: 'Щоб увімкнути онлайн-оплату, потрібні логін мерчанта, домен і секретний ключ',
    ordersInFlight: (count: number, until: string | null) =>
      `Зараз ${orders(count)} ще можуть оплатити на цьому мерчанті — змінити логін чи ключ можна${until ? ` після ${until}` : ' трохи згодом'}. Вимкнути онлайн-оплату можна й зараз`,
    paymentsKeyMissing: 'Онлайн-оплата на сервері ще не налаштована. Напишіть команді eatlete',
  },
}

export type SaleStrings = typeof saleUk
